import type { ProfileResponse } from '@shared/schemas';

import api from '@/shared/api/api-client';
import type { FilterGroup } from '@/types/filters';

import type { ProfileQueryGateway } from './profile-query';

/** `POST /profiles/by-ids` refuses more ids than this in one body (`maxItems`). */
const BY_IDS_MAX = 5000;

/**
 * Every profile of `ids`, read in requests of at most {@link BY_IDS_MAX} ids;
 * a single request over the cap is a 400, which used to blank the enrichment
 * buckets of a big import (#621).
 */
async function fetchAllByIds(ids: string[]): Promise<ProfileResponse[]> {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += BY_IDS_MAX) chunks.push(ids.slice(i, i + BY_IDS_MAX));
  const pages = await Promise.all(
    chunks.map(
      async (chunk) =>
        (await api.post<ProfileResponse[]>('/profiles/by-ids', { ids: chunk, limit: BY_IDS_MAX })) ??
        [],
    ),
  );
  return pages.flat();
}

/**
 * Real HTTP implementation of the shared profile-selection data layer. Path
 * building is kept 1:1 with the original hooks (useInfiniteApi /
 * useDuplicateCount / the enrichment-bucket fetch) so the server sees identical
 * requests, and both feature gateways spread this in.
 */
export const profileQueryHttp: ProfileQueryGateway = {
  fetchProfiles: async ({ filters, page, pageSize, excludeDuplicates, licenceId }) => {
    const basePath = excludeDuplicates
      ? `/profiles?exclude_duplicates=true${licenceId ? `&exclude_licence_id=${licenceId}` : ''}`
      : '/profiles';
    const serverPage = page + 1; // backend is 1-indexed
    const sep = basePath.includes('?') ? '&' : '?';
    let path = `${basePath}${sep}page=${serverPage}&limit=${pageSize}`;
    if (filters) path += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;

    const res = await api.getRaw<ProfileResponse[]>(path);
    const rows = res.data ?? [];
    return { rows, total: res.pagination?.total ?? rows.length };
  },

  countRawTotal: async (filters: FilterGroup | null) => {
    const params = new URLSearchParams({ limit: '1', page: '1' });
    if (filters) params.set('filters', JSON.stringify(filters));
    const res = await api.getRaw<unknown>(`/profiles?${params.toString()}`);
    return res.pagination?.total ?? 0;
  },

  fetchForBuckets: async ({
    filters,
    excludeDuplicates,
    importedIds,
    licenceId,
    includeFilterResults,
  }) => {
    // No "select profiles" method ⇒ no filter-derived profiles at all. Asking
    // with `filters: null` would instead return the first 5000 rows of the
    // database and quietly grow the list (docs/product-issues.md PI-40).
    if (!includeFilterResults) {
      return { filterProfiles: [], rawTotal: 0, importedProfiles: await fetchAllByIds(importedIds) };
    }

    const baseParams = new URLSearchParams({ limit: '5000' });
    if (filters) baseParams.set('filters', JSON.stringify(filters));

    const filteredParams = new URLSearchParams(baseParams);
    if (excludeDuplicates) {
      filteredParams.set('exclude_duplicates', 'true');
      if (licenceId) filteredParams.set('exclude_licence_id', licenceId);
    }

    // Order matches the original hook: filtered fetch first, then raw total, then imports.
    const [filteredRes, rawRes, importedProfiles] = await Promise.all([
      api.getRaw<ProfileResponse[]>(`/profiles?${filteredParams.toString()}`),
      excludeDuplicates
        ? (() => {
            const rawTotalParams = new URLSearchParams(baseParams);
            rawTotalParams.set('limit', '1');
            return api.getRaw<unknown>(`/profiles?${rawTotalParams.toString()}`);
          })()
        : Promise.resolve(null),
      fetchAllByIds(importedIds),
    ]);

    const filterProfiles = filteredRes.data ?? [];
    const rawTotal = rawRes
      ? (rawRes.pagination?.total ?? filterProfiles.length)
      : filterProfiles.length;
    return { filterProfiles, rawTotal, importedProfiles };
  },

  fetchProfilesByIds: async (ids, page, pageSize) => {
    if (ids.length === 0) return { rows: [], total: 0 };
    const res = await api.postRaw<ProfileResponse[]>('/profiles/by-ids', {
      ids,
      page: page + 1, // backend is 1-indexed
      limit: pageSize,
    });
    const rows = res.data ?? [];
    return { rows, total: res.pagination?.total ?? ids.length };
  },

  analyzeScope: async (query: string) => {
    const res = await api.post<{ filters: FilterGroup; interpretation: string }>(
      '/profiles/nl-search',
      { query },
    );
    return res.filters;
  },
};
